import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DailyIframe from "@daily-co/daily-js";
import { toast } from "sonner";
import { Hand, StickyNote } from "lucide-react";
import { api, isUnreachableError } from "../../lib/api";
import { showServiceUnavailable } from "../../components/ServiceUnavailableToast";
import { Brand } from "../../components/Logo";
import MeetingNotesPanel from "./MeetingNotesPanel";

/**
 * FIX RIUNIONI 2026-11-02 — Full join page redesign:
 * - Theme Daily esteso (alto contrasto icone tray)
 * - Banner countdown 5min prima della fine
 * - Bottone "Estendi +1h" (chiama backend POST /meetings/:id/extend)
 * - Custom tray button "Alza la mano" via setCustomTrayButtons (+ broadcast
 *   sendAppMessage ai partecipanti). Overlay "✋" sul tile locale quando attivo.
 * - Service unavailable toast se backend non raggiungibile
 */

// Z-index topbar/banner — SOPRA l'iframe Daily (che è z-index: auto per
// default). Non usiamo fullscreen API browser perché richiede gesture;
// la pagina occupa già 100vw/100vh via AppShell/PortalShell detect in-call.
const BANNER_ZINDEX = 1_000_000;

const RAISEHAND_ICON_SVG = encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
     stroke="#EFEFEC" stroke-width="1.8" stroke-linecap="round"
     stroke-linejoin="round">
  <path d="M18 11V6a2 2 0 1 0-4 0v5"/>
  <path d="M14 11V4a2 2 0 1 0-4 0v7"/>
  <path d="M10 11V5a2 2 0 1 0-4 0v9"/>
  <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/>
</svg>
`.trim());

export default function RiunioneJoin({ userType = "internal" }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const containerRef = useRef(null);
    const callFrameRef = useRef(null);
    const setupRanRef = useRef(false);
    const meetingRef = useRef(null);

    const [status, setStatus] = useState("loading"); // loading | joined | error
    const [errorMsg, setErrorMsg] = useState("");
    const [scheduledEnd, setScheduledEnd] = useState(null); // Date | null
    const [showEndingBanner, setShowEndingBanner] = useState(false);
    const [minutesLeft, setMinutesLeft] = useState(0);
    const [bannerDismissedAt, setBannerDismissedAt] = useState(0);
    const [extending, setExtending] = useState(false);
    const [handRaised, setHandRaised] = useState(false);
    const [notesOpen, setNotesOpen] = useState(false);
    // Rilevazione mobile viewport (<768px) per fullscreen panel
    const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const exitTo = userType === "internal" ? "/app/riunioni" : "/portal/riunioni";

    // =========================================================================
    // Banner countdown logic (tick ogni 15s)
    // =========================================================================
    useEffect(() => {
        if (!scheduledEnd) return;
        const tick = () => {
            const diffMin = (scheduledEnd.getTime() - Date.now()) / 60000;
            setMinutesLeft(Math.max(0, Math.ceil(diffMin)));
            // mostra banner a -5min; se dismissato, ricompare a -1min
            const showAtFive = diffMin <= 5 && diffMin > 0;
            const dismissTooOld = Date.now() - bannerDismissedAt > 4 * 60_000;
            if (showAtFive && (bannerDismissedAt === 0 || diffMin <= 1 || dismissTooOld)) {
                setShowEndingBanner(true);
            } else if (diffMin <= 0 || diffMin > 5) {
                setShowEndingBanner(false);
            }
        };
        tick();
        const h = setInterval(tick, 15_000);
        return () => clearInterval(h);
    }, [scheduledEnd, bannerDismissedAt]);

    // =========================================================================
    // Extend +1h
    // =========================================================================
    const doExtend = useCallback(async () => {
        if (extending) return;
        setExtending(true);
        try {
            const { data } = await api.post(`/internal/meetings/${id}/extend`);
            if (data?.datetime_end) {
                const d = new Date(data.datetime_end);
                setScheduledEnd(d);
                meetingRef.current = { ...meetingRef.current, datetime_end: data.datetime_end };
                toast.success(
                    `Riunione estesa fino a ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`,
                );
            }
            setShowEndingBanner(false);
            setBannerDismissedAt(Date.now());
        } catch (err) {
            if (isUnreachableError(err)) {
                showServiceUnavailable(doExtend);
            } else if (err?.response?.status === 403) {
                toast.error("Non autorizzato a estendere questa riunione");
            } else {
                toast.error(err?.response?.data?.detail || "Errore estensione");
            }
        } finally {
            setExtending(false);
        }
    }, [extending, id]);

    // =========================================================================
    // Setup Daily (una sola volta)
    // =========================================================================
    useEffect(() => {
        if (setupRanRef.current) return;
        setupRanRef.current = true;
        let mounted = true;
        let callFrame = null;

        const setup = async () => {
            if (!containerRef.current) {
                if (mounted) { setStatus("error"); setErrorMsg("Container non pronto"); }
                return;
            }
            try {
                // Fetch dettaglio meeting per scheduled_end
                const basePath = userType === "internal" ? "/internal" : "/client";
                try {
                    const { data: m } = await api.get(`${basePath}/meetings/${id}`);
                    meetingRef.current = m;
                    if (m?.datetime_end) setScheduledEnd(new Date(m.datetime_end));
                } catch (e) {
                    // non-blocking; se fallisce il countdown non si attiva
                    // eslint-disable-next-line no-console
                    console.warn("[RiunioneJoin] get meeting failed", e?.message);
                }

                const { data } = await api.post(`/meetings/${id}/join-token`, {});
                if (!mounted) return;
                if (!data?.room_url || !data?.token) {
                    setStatus("error"); setErrorMsg("Risposta non valida dal server"); return;
                }
                if (!containerRef.current || !document.body.contains(containerRef.current)) {
                    setStatus("error"); setErrorMsg("Container non disponibile"); return;
                }
                try {
                    containerRef.current.querySelectorAll("iframe").forEach(f => f.remove());
                } catch (_) { /* noop */ }

                callFrame = DailyIframe.createFrame(containerRef.current, {
                    iframeStyle: {
                        width: "100%",
                        height: "100%",
                        border: "0",
                        display: "block",
                    },
                    showLeaveButton: true,
                    // Theme alto contrasto — icone tray chiare su bg scuro.
                    // Riferimento proprietà: @daily-co/daily-js v0.79.0
                    theme: {
                        colors: {
                            accent: "#d9f24f",
                            accentText: "#0F172A",
                            background: "#181B20",
                            backgroundAccent: "#262a30",
                            baseText: "#EFEFEC",
                            border: "#2a2e35",
                            mainAreaBg: "#000000",
                            mainAreaBgAccent: "#181B20",
                            mainAreaText: "#EFEFEC",
                            supportiveText: "#94A3B8",
                        },
                    },
                });
                callFrameRef.current = callFrame;

                // Custom tray button: "Alza la mano" — Daily v0.79 API
                try {
                    const setter = callFrame.setCustomTrayButtons
                        || callFrame.updateCustomTrayButtons;
                    if (typeof setter === "function") {
                        setter.call(callFrame, {
                            raisehand: {
                                iconPath: `data:image/svg+xml;utf8,${RAISEHAND_ICON_SVG}`,
                                label: "Alza la mano",
                                tooltip: "Alza/abbassa la mano",
                            },
                        });
                    }
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn("[RiunioneJoin] custom tray button not supported:", e?.message);
                }

                // Debug globale
                callFrame.on("*", (ev) => {
                    // eslint-disable-next-line no-console
                    if (ev && ev.action && ev.action !== "network-quality-change") {
                        console.log("[daily]", ev.action);
                    }
                });
                callFrame.on("loaded", () => { if (mounted) setStatus("joined"); });
                callFrame.on("joined-meeting", () => { if (mounted) setStatus("joined"); });
                callFrame.on("error", (ev) => {
                    // eslint-disable-next-line no-console
                    console.error("[Daily error]", ev);
                    if (mounted) {
                        setStatus("error");
                        setErrorMsg((ev && (ev.errorMsg || ev.error)) || "Errore Daily");
                    }
                });
                callFrame.on("left-meeting", () => navigate(exitTo));

                // Custom button click
                callFrame.on("custom-button-click", (ev) => {
                    if (ev?.button_id === "raisehand") {
                        setHandRaised((prev) => {
                            const next = !prev;
                            try {
                                callFrame.sendAppMessage({ t: "raisehand", on: next }, "*");
                            } catch (_) { /* noop */ }
                            toast.info(next ? "Mano alzata ✋" : "Mano abbassata",
                                       { duration: 2000 });
                            return next;
                        });
                    }
                });

                // Ricevi broadcast raisehand dagli altri partecipanti → toast info
                callFrame.on("app-message", (ev) => {
                    const m = ev?.data;
                    if (!m || m.t !== "raisehand") return;
                    const from = ev.fromId || "partecipante";
                    toast.info(m.on ? `${from.slice(0, 8)}… ha alzato la mano ✋`
                                     : `${from.slice(0, 8)}… ha abbassato la mano`,
                               { duration: 3000 });
                });

                await callFrame.join({ url: data.room_url, token: data.token });
            } catch (err) {
                if (!mounted) return;
                const code = err?.response?.status;
                if (code === 404) {
                    toast.error("Riunione non trovata o accesso negato");
                    navigate(exitTo);
                    return;
                }
                if (isUnreachableError(err)) {
                    setStatus("error");
                    setErrorMsg("Servizio non raggiungibile. Riprova.");
                    showServiceUnavailable(() => window.location.reload());
                    return;
                }
                const detail = err?.response?.data?.detail;
                if (code === 503) setErrorMsg("Room non disponibile — riprova più tardi");
                else setErrorMsg(detail || err?.message || "Errore durante l'accesso alla riunione");
                setStatus("error");
            }
        };

        setup();

        return () => {
            mounted = false;
            if (callFrame) {
                try { callFrame.destroy(); } catch (_) { /* noop */ }
            }
            if (containerRef.current) {
                try {
                    containerRef.current.querySelectorAll("iframe").forEach(f => f.remove());
                } catch (_) { /* noop */ }
            }
            callFrameRef.current = null;
            setupRanRef.current = false;
        };
    }, [id, navigate, exitTo, userType]);

    // =========================================================================
    // Render
    // =========================================================================
    const canExtend = userType === "internal";
    return (
        <div
            data-testid="riunione-join-page"
            style={{
                height: "100vh", minHeight: "100vh",
                display: "flex", flexDirection: "column",
                background: "#181B20", overflow: "hidden",
            }}
        >
            {/* Topbar leggera — minimal brand + bottone Esci.
                SOPRA Daily tray (z-index) ma non copre i controlli perché è
                in alto e il tray Daily è in basso. */}
            <div style={{
                height: 56, flexShrink: 0,
                background: "#181B20", color: "#FFFFFF",
                display: "flex", alignItems: "center",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                padding: "0 20px", gap: 16,
                zIndex: BANNER_ZINDEX, position: "relative",
            }}>
                <Brand />
                {handRaised && (
                    <div data-testid="riunione-hand-badge"
                         style={{
                             display: "inline-flex", alignItems: "center", gap: 6,
                             padding: "4px 10px", borderRadius: 999,
                             background: "#d9f24f", color: "#0F172A",
                             fontFamily: "'IBM Plex Mono', monospace",
                             fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
                             textTransform: "uppercase",
                         }}>
                        <Hand size={12} /> Mano alzata
                    </div>
                )}
                <div style={{ flex: 1 }} />
                {userType === "internal" && (
                    <button
                        type="button"
                        onClick={() => setNotesOpen((v) => !v)}
                        style={{
                            background: notesOpen ? "#d9f24f" : "transparent",
                            color: notesOpen ? "#0F172A" : "#FFFFFF",
                            border: "1px solid rgba(255,255,255,0.24)", padding: "6px 14px",
                            cursor: "pointer", fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase",
                            display: "inline-flex", alignItems: "center", gap: 6,
                            marginRight: 8, fontWeight: 600,
                        }}
                        data-testid="riunione-notes-toggle-btn"
                        aria-pressed={notesOpen}
                    >
                        <StickyNote size={13} /> Note
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => navigate(exitTo)}
                    style={{
                        background: "transparent", color: "#FFFFFF",
                        border: "1px solid rgba(255,255,255,0.24)", padding: "6px 14px",
                        cursor: "pointer", fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase",
                    }}
                    data-testid="riunione-exit-btn"
                >
                    ← Esci
                </button>
            </div>

            {/* Banner countdown -5min → SOPRA Daily iframe */}
            {showEndingBanner && (
                <div
                    data-testid="riunione-ending-banner"
                    style={{
                        position: "absolute", top: 56, left: 0, right: 0,
                        zIndex: BANNER_ZINDEX,
                        background: "#F59E0B", color: "#0F172A",
                        padding: "12px 20px",
                        display: "flex", alignItems: "center", gap: 16,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                    }}
                >
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                        La riunione termina tra {minutesLeft} {minutesLeft === 1 ? "minuto" : "minuti"}
                    </div>
                    <div style={{ flex: 1 }} />
                    {canExtend && (
                        <button
                            type="button"
                            onClick={doExtend}
                            disabled={extending}
                            data-testid="riunione-extend-btn"
                            style={{
                                background: "#0F172A", color: "#d9f24f",
                                border: "none", padding: "8px 16px",
                                cursor: extending ? "wait" : "pointer",
                                fontFamily: "'IBM Plex Mono', monospace",
                                fontSize: 12, fontWeight: 600,
                                letterSpacing: "0.08em", textTransform: "uppercase",
                            }}
                        >
                            {extending ? "..." : "Estendi +1h"}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => {
                            setShowEndingBanner(false);
                            setBannerDismissedAt(Date.now());
                        }}
                        data-testid="riunione-ending-dismiss-btn"
                        style={{
                            background: "transparent", color: "#0F172A",
                            border: "1px solid rgba(15,23,42,0.3)",
                            padding: "6px 12px", cursor: "pointer",
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase",
                        }}
                    >
                        Chiudi
                    </button>
                </div>
            )}

            {/* Area iframe */}
            <div style={{
                flex: "1 1 auto", minHeight: 0,
                position: "relative", width: "100%",
            }}>
                <div
                    ref={containerRef}
                    data-testid="riunione-daily-container"
                    style={{ width: "100%", height: "100%", position: "relative" }}
                />
                {(status === "loading" || status === "error") && (
                    <div
                        style={{
                            position: "absolute", inset: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            background: "#181B20", color: "#EFEFEC", zIndex: 10,
                            textAlign: "center", padding: 24,
                            pointerEvents: status === "error" ? "auto" : "none",
                        }}
                        data-testid={status === "loading" ? "riunione-loading" : "riunione-error"}
                    >
                        {status === "loading" ? (
                            <div>Connessione alla riunione…</div>
                        ) : (
                            <div>
                                <div style={{ color: "#FF6B6B", marginBottom: 12, fontSize: 16 }}>
                                    Impossibile avviare la riunione
                                </div>
                                <div style={{ color: "#8A8F88", fontSize: 13, marginBottom: 20 }}>
                                    {errorMsg}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => navigate(exitTo)}
                                    style={{
                                        background: "#d9f24f", color: "#0F172A",
                                        border: "none", padding: "8px 18px", cursor: "pointer",
                                        fontFamily: "'IBM Plex Mono', monospace",
                                        fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase",
                                    }}
                                    data-testid="riunione-error-back-btn"
                                >
                                    Torna alla lista
                                </button>
                            </div>
                        )}
                    </div>
                )}
                {userType === "internal" && (
                    <MeetingNotesPanel
                        meetingId={id}
                        meeting={meetingRef.current}
                        open={notesOpen}
                        onClose={() => setNotesOpen(false)}
                        variant="overlay"
                        mobile={isMobile}
                    />
                )}
            </div>
        </div>
    );
}
